import { useTranslation } from "react-i18next"

export type Translations = typeof defaultTranslations

const defaultTranslations = {
  ReadAnyCustomerDescription: "Can view name and contact details of any customer in this organization",
  ReadAnySupplierDescription: "Can view name and contact details of any supplier in this organization",
  ReadAnyWorkspaceDescription: "Can view name and member details of any workspace in this organization",
  ReadAnyTaskDescription: "Can view details of any task in this organization",
  ReadAnyProjectDescription: "Can view details of any project in this organization",
  ReadAnyCalendarEventDescription: "Can view details of any calendar event in this organization",
  ReadAnyTodoDescription: "Can view details of any todo in this organization",
  ReadProjectDescription: "Can view details of the project",
  ReadProjectTaskDescription: "Can view details of any task connected to the project",
  ReadTaskDescription: "Can view details of the task",
  orgMemberRoleApplyToAllBtnLabel: "Apply these permissions to all members",
  projectManagerRoleApplyToAllBtnLabel: "Apply these permissions to all project managers",
  projectParticipantRoleApplyToAllBtnLabel: "Apply these permissions to all project participants",
  taskManagerRoleApplyToAllBtnLabel: "Apply these permissions to all task managers",
  taskParticipantRoleApplyToAllBtnLabel: "Apply these permissions to all task participants",
  customerPermissionsHeader: "Customer permissions",
  supplierPermissionsHeader: "Supplier permissions",
  workspacePermissionsHeader: "Workspace permissions",
  membershipPermissionsHeader: "Member permissions",
  invitationPermissionsHeader: "Invitation permissions",
  projectPermissionsHeader: "Project permissions",
  taskPermissionsHeader: "Task permissions",
  calendarEventPermissionsHeader: "Calendar event permissions",
  todoPermissionsHeader: "To-do permissions",
  organisationPermissionsHeader: "Organization permissions",
  CreateCustomersDescription: "Can create and update customers in this organization",
  CreateSuppliersDescription: "Can create and update suppliers in this organization",
  CreateWorkspacesDescription: "Can create and update workspaces in this organization",
  CreateMembershipsDescription: "Can add members to this organization and manage their permissions",
  ReadAnyNormalMembershipDetailsDescription:
    "Can view name and contact details of any standard member of this organization",
  ReadAnyLimitedMembershipDetailsDescription:
    "Can view name and contact details of any limited member of this organization",
  UpdateAnyMembershipDetailsDescription: "Can update firstname, lastname and email of any member of this organization",
  UpdateAnyMembershipPermissionsDescription: "Can update permissions of any member of this organization",
  UpdateAnyMembershipStatusDescription: "Can activate or deactivate members of this organization",
  CreateInvitationsDescription: "Can invite new members to this organization",
  ReadAnyInvitationDescription: "Can read invitations sent to new members of this organization",
  DeleteAnyInvitationDescription: "Can delete invitations sent to new members of this organization",
  CreateProjectsDescription: "Can create projects in this organization",
  UpdateAnyProjectDetailsDescription: "Can update title, number and description of any project in this organization",
  UpdateAnyProjectPlanDescription: "Can update schedule of any project in this organization",
  UpdateAnyProjectStatusDescription: "Can update status of any project in this organization",
  UpdateAnyProjectManagersDescription: "Can update managers of any project in this organization",
  UpdateAnyProjectParticipantsDescription: "Can update participants of any project in this organization",
  UpdateAnyProjectCustomersDescription: "Can update customers of any project in this organization",
  UpdateAnyProjectSuppliersDescription: "Can update suppliers of any project in this organization",
  UpdateAnyProjectWorkspacesDescription: "Can update workspaces of any project in this organization",
  DeleteAnyProjectDescription: "Can delete and archive any project in this organization",
  CreateTasksDescription: "Can create tasks in all projects of this organization",
  UpdateAnyTaskDetailsDescription: "Can update title, number and description of any task in this organization",
  UpdateAnyTaskPlanDescription: "Can update schedule of any task in this organization",
  UpdateAnyTaskStatusDescription: "Can update status of any task in this organization",
  UpdateAnyTaskManagersDescription: "Can update managers of any task in this organization",
  UpdateAnyTaskParticipantsDescription: "Can update participants of any task in this organization",
  UpdateAnyTaskSuppliersDescription: "Can update suppliers of any task in this organization",
  UpdateAnyTaskWorkspacesDescription: "Can update workspaces of any task in this organization",
  DeleteAnyTaskDescription: "Can delete and archive any task in this organization",
  CreateCalendarEventsDescription: "Can create calendar events in this organization",
  UpdateAnyCalendarEventDescription: "Can update information about any calendar event in this organization",
  CreateTodosDescription: "Can create todos in this organization",
  UpdateAnyTodoDescription: "Can update information about any todo in this organization",
  UpdateOrganisationDetailsDescription: "Can update general and notification settings of this organization",
  UpdateProjectDetailsDescription: "Can update title, number and description of the project",
  UpdateProjectPlanDescription: "Can update schedule of the project",
  UpdateProjectStatusDescription: "Can update status of the project",
  UpdateProjectManagersDescription: "Can update managers of the project",
  UpdateProjectParticipantsDescription: "Can update participants of the project",
  UpdateProjectCustomersDescription: "Can update customers of the project",
  UpdateProjectSuppliersDescription: "Can update suppliers of the project",
  UpdateProjectWorkspacesDescription: "Can update workspaces of the project",
  UpdateProjectPermissionsDescription: "Can update permissions of the project",
  DeleteProjectDescription: "Can delete the project",
  CreateProjectTasksDescription: "Can create tasks in the project",
  UpdateProjectTaskDetailsDescription: "Can update title, number and description of any tasks connected to the project",
  UpdateProjectTaskStatusDescription: "Can update status of any tasks connected to the project",
  UpdateProjectTaskManagersDescription: "Can update managers of any tasks connected to the project",
  UpdateProjectTaskParticipantsDescription: "Can update participants of any tasks connected to the project",
  UpdateProjectTaskSuppliersDescription: "Can update suppliers of any tasks connected to the project",
  UpdateProjectTaskWorkspacesDescription: "Can update workspaces of any tasks connected to the project",
  DeleteProjectTaskDescription: "Can delete any tasks connected to the project",
  CreateProjectCalendarEventsDescription: "Can create calendar events in the project",
  CreateProjectTodosDescription: "Can create to-dos in the project",
  CreateSubTasksDescription: "Can create sub-tasks in the task",
  UpdateTaskDetailsDescription: "Can update title, number and description of the task",
  UpdateTaskStatusDescription: "Can update status of the task",
  UpdateTaskManagersDescription: "Can update managers of the task",
  UpdateTaskParticipantsDescription: "Can update participants of the task",
  UpdateTaskSuppliersDescription: "Can update suppliers of the task",
  UpdateTaskWorkspacesDescription: "Can update workspaces of the task",
  DeleteTaskDescription: "Can delete the task",
  CreateTaskCalendarEventsDescription: "Can create calendar events in the task",
  CreateTaskTodosDescription: "Can create to-dos in the task",
  UpdateAnyRoleDescription:
    "Can update default permissions of members, project managers or participants, task managers or participants",
  ManageProjectNotificationsDescription: "Can manage notifications of the project",
  yesBtnLabel: "Yes",
  noBtnLabel: "No",
  ViewProjectAttachmentsDescription: "Can view attachments of the project",
  UpdateProjectAttachmentsDescription: "Can update attachments of the project",
  ViewTaskAttachmentsDescription: "Can view attachments of the task",
  UpdateTaskAttachmentsDescription: "Can update attachments of the task",
  ViewAnyProjectAttachmentsDescription: "Can view attachments of any project in this organization",
  UpdateAnyProjectAttachmentsDescription: "Can update attachments of any project in this organization",
  ViewAnyTaskAttachmentsDescription: "Can view attachments of any task in this organization",
  UpdateAnyTaskAttachmentsDescription: "Can update attachments of any task in this organization",
  hourReportPermissionsHeader: "Hour report permissions",
  ReportOwnHoursAsMemberDescription: "Can report own hours in projects and tasks where this user is a member",
  ReportOwnHoursAnywhereDescription: "Can report own hours in any projects and tasks in the organization",
  ReportOthersHoursAsMemberDescription:
    "Can report hours of other members in projects and tasks where this user is a member",
  ReportOthersHoursAnywhereDescription:
    "Can report hours of other members in any projects and tasks in the organization",
  ViewAnyHourReportDescription: "Can view all hours reported in the organization",
}

const useTranslations = (): Translations => {
  const { t } = useTranslation()
  const { t: tPermission } = useTranslation("permission")

  return {
    ReadAnyCustomerDescription: tPermission("ReadAnyCustomerDescription", {
      defaultValue: defaultTranslations.ReadAnyCustomerDescription,
    }),
    ReadAnySupplierDescription: tPermission("ReadAnySupplierDescription", {
      defaultValue: defaultTranslations.ReadAnySupplierDescription,
    }),
    ReadAnyWorkspaceDescription: tPermission("ReadAnyWorkspaceDescription", {
      defaultValue: defaultTranslations.ReadAnyWorkspaceDescription,
    }),
    ReadAnyTaskDescription: tPermission("ReadAnyTaskDescription", {
      defaultValue: defaultTranslations.ReadAnyTaskDescription,
    }),
    ReadAnyProjectDescription: tPermission("ReadAnyProjectDescription", {
      defaultValue: defaultTranslations.ReadAnyProjectDescription,
    }),
    ReadAnyCalendarEventDescription: tPermission("ReadAnyCalendarEventDescription", {
      defaultValue: defaultTranslations.ReadAnyCalendarEventDescription,
    }),
    ReadAnyTodoDescription: tPermission("ReadAnyTodoDescription", {
      defaultValue: defaultTranslations.ReadAnyTodoDescription,
    }),
    ReadProjectDescription: tPermission("ReadProjectDescription", {
      defaultValue: defaultTranslations.ReadProjectDescription,
    }),
    ReadProjectTaskDescription: tPermission("ReadProjectTaskDescription", {
      defaultValue: defaultTranslations.ReadProjectTaskDescription,
    }),
    ReadTaskDescription: tPermission("ReadTaskDescription", { defaultValue: defaultTranslations.ReadTaskDescription }),
    orgMemberRoleApplyToAllBtnLabel: tPermission("orgMemberRoleApplyToAllBtnLabel", {
      defaultValue: defaultTranslations.orgMemberRoleApplyToAllBtnLabel,
    }),
    projectManagerRoleApplyToAllBtnLabel: tPermission("projectManagerRoleApplyToAllBtnLabel", {
      defaultValue: defaultTranslations.projectManagerRoleApplyToAllBtnLabel,
    }),
    projectParticipantRoleApplyToAllBtnLabel: tPermission("projectParticipantRoleApplyToAllBtnLabel", {
      defaultValue: defaultTranslations.projectParticipantRoleApplyToAllBtnLabel,
    }),
    taskManagerRoleApplyToAllBtnLabel: tPermission("taskManagerRoleApplyToAllBtnLabel", {
      defaultValue: defaultTranslations.taskManagerRoleApplyToAllBtnLabel,
    }),
    taskParticipantRoleApplyToAllBtnLabel: tPermission("taskParticipantRoleApplyToAllBtnLabel", {
      defaultValue: defaultTranslations.taskParticipantRoleApplyToAllBtnLabel,
    }),
    customerPermissionsHeader: tPermission("customerPermissionsHeader", {
      defaultValue: defaultTranslations.customerPermissionsHeader,
    }),
    supplierPermissionsHeader: tPermission("supplierPermissionsHeader", {
      defaultValue: defaultTranslations.supplierPermissionsHeader,
    }),
    workspacePermissionsHeader: tPermission("workspacePermissionsHeader", {
      defaultValue: defaultTranslations.workspacePermissionsHeader,
    }),
    membershipPermissionsHeader: tPermission("membershipPermissionsHeader", {
      defaultValue: defaultTranslations.membershipPermissionsHeader,
    }),
    invitationPermissionsHeader: tPermission("invitationPermissionsHeader", {
      defaultValue: defaultTranslations.invitationPermissionsHeader,
    }),
    projectPermissionsHeader: tPermission("projectPermissionsHeader", {
      defaultValue: defaultTranslations.projectPermissionsHeader,
    }),
    taskPermissionsHeader: tPermission("taskPermissionsHeader", {
      defaultValue: defaultTranslations.taskPermissionsHeader,
    }),
    calendarEventPermissionsHeader: tPermission("calendarEventPermissionsHeader", {
      defaultValue: defaultTranslations.calendarEventPermissionsHeader,
    }),
    todoPermissionsHeader: tPermission("todoPermissionsHeader", {
      defaultValue: defaultTranslations.todoPermissionsHeader,
    }),
    organisationPermissionsHeader: tPermission("organisationPermissionsHeader", {
      defaultValue: defaultTranslations.organisationPermissionsHeader,
    }),
    CreateCustomersDescription: tPermission("CreateCustomersDescription", {
      defaultValue: defaultTranslations.CreateCustomersDescription,
    }),
    CreateSuppliersDescription: tPermission("CreateSuppliersDescription", {
      defaultValue: defaultTranslations.CreateSuppliersDescription,
    }),
    CreateWorkspacesDescription: tPermission("CreateWorkspacesDescription", {
      defaultValue: defaultTranslations.CreateWorkspacesDescription,
    }),
    CreateMembershipsDescription: tPermission("CreateMembershipsDescription", {
      defaultValue: defaultTranslations.CreateMembershipsDescription,
    }),
    ReadAnyNormalMembershipDetailsDescription: tPermission("ReadAnyNormalMembershipDetailsDescription", {
      defaultValue: defaultTranslations.ReadAnyNormalMembershipDetailsDescription,
    }),
    ReadAnyLimitedMembershipDetailsDescription: tPermission("ReadAnyLimitedMembershipDetailsDescription", {
      defaultValue: defaultTranslations.ReadAnyLimitedMembershipDetailsDescription,
    }),
    UpdateAnyMembershipDetailsDescription: tPermission("UpdateAnyMembershipDetailsDescription", {
      defaultValue: defaultTranslations.UpdateAnyMembershipDetailsDescription,
    }),
    UpdateAnyMembershipPermissionsDescription: tPermission("UpdateAnyMembershipPermissionsDescription", {
      defaultValue: defaultTranslations.UpdateAnyMembershipPermissionsDescription,
    }),
    UpdateAnyMembershipStatusDescription: tPermission("UpdateAnyMembershipStatusDescription", {
      defaultValue: defaultTranslations.UpdateAnyMembershipStatusDescription,
    }),
    CreateInvitationsDescription: tPermission("CreateInvitationsDescription", {
      defaultValue: defaultTranslations.CreateInvitationsDescription,
    }),
    ReadAnyInvitationDescription: tPermission("ReadAnyInvitationDescription", {
      defaultValue: defaultTranslations.ReadAnyInvitationDescription,
    }),
    DeleteAnyInvitationDescription: tPermission("DeleteAnyInvitationDescription", {
      defaultValue: defaultTranslations.DeleteAnyInvitationDescription,
    }),
    CreateProjectsDescription: tPermission("CreateProjectsDescription", {
      defaultValue: defaultTranslations.CreateProjectsDescription,
    }),
    UpdateAnyProjectDetailsDescription: tPermission("UpdateAnyProjectDetailsDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectDetailsDescription,
    }),
    UpdateAnyProjectPlanDescription: tPermission("UpdateAnyProjectPlanDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectPlanDescription,
    }),
    UpdateAnyProjectStatusDescription: tPermission("UpdateAnyProjectStatusDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectStatusDescription,
    }),
    UpdateAnyProjectManagersDescription: tPermission("UpdateAnyProjectManagersDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectManagersDescription,
    }),
    UpdateAnyProjectParticipantsDescription: tPermission("UpdateAnyProjectParticipantsDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectParticipantsDescription,
    }),
    UpdateAnyProjectCustomersDescription: tPermission("UpdateAnyProjectCustomersDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectCustomersDescription,
    }),
    UpdateAnyProjectSuppliersDescription: tPermission("UpdateAnyProjectSuppliersDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectSuppliersDescription,
    }),
    UpdateAnyProjectWorkspacesDescription: tPermission("UpdateAnyProjectWorkspacesDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectWorkspacesDescription,
    }),
    DeleteAnyProjectDescription: tPermission("DeleteAnyProjectDescription", {
      defaultValue: defaultTranslations.DeleteAnyProjectDescription,
    }),
    CreateTasksDescription: tPermission("CreateTasksDescription", {
      defaultValue: defaultTranslations.CreateTasksDescription,
    }),
    UpdateAnyTaskDetailsDescription: tPermission("UpdateAnyTaskDetailsDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskDetailsDescription,
    }),
    UpdateAnyTaskPlanDescription: tPermission("UpdateAnyTaskPlanDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskPlanDescription,
    }),
    UpdateAnyTaskStatusDescription: tPermission("UpdateAnyTaskStatusDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskStatusDescription,
    }),
    UpdateAnyTaskManagersDescription: tPermission("UpdateAnyTaskManagersDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskManagersDescription,
    }),
    UpdateAnyTaskParticipantsDescription: tPermission("UpdateAnyTaskParticipantsDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskParticipantsDescription,
    }),
    UpdateAnyTaskSuppliersDescription: tPermission("UpdateAnyTaskSuppliersDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskSuppliersDescription,
    }),
    UpdateAnyTaskWorkspacesDescription: tPermission("UpdateAnyTaskWorkspacesDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskWorkspacesDescription,
    }),
    DeleteAnyTaskDescription: tPermission("DeleteAnyTaskDescription", {
      defaultValue: defaultTranslations.DeleteAnyTaskDescription,
    }),
    CreateCalendarEventsDescription: tPermission("CreateCalendarEventsDescription", {
      defaultValue: defaultTranslations.CreateCalendarEventsDescription,
    }),
    UpdateAnyCalendarEventDescription: tPermission("UpdateAnyCalendarEventDescription", {
      defaultValue: defaultTranslations.UpdateAnyCalendarEventDescription,
    }),
    CreateTodosDescription: tPermission("CreateTodosDescription", {
      defaultValue: defaultTranslations.CreateTodosDescription,
    }),
    UpdateAnyTodoDescription: tPermission("UpdateAnyTodoDescription", {
      defaultValue: defaultTranslations.UpdateAnyTodoDescription,
    }),
    UpdateOrganisationDetailsDescription: tPermission("UpdateOrganisationDetailsDescription", {
      defaultValue: defaultTranslations.UpdateOrganisationDetailsDescription,
    }),
    UpdateProjectDetailsDescription: tPermission("UpdateProjectDetailsDescription", {
      defaultValue: defaultTranslations.UpdateProjectDetailsDescription,
    }),
    UpdateProjectPlanDescription: tPermission("UpdateProjectPlanDescription", {
      defaultValue: defaultTranslations.UpdateProjectPlanDescription,
    }),
    UpdateProjectStatusDescription: tPermission("UpdateProjectStatusDescription", {
      defaultValue: defaultTranslations.UpdateProjectStatusDescription,
    }),
    UpdateProjectManagersDescription: tPermission("UpdateProjectManagersDescription", {
      defaultValue: defaultTranslations.UpdateProjectManagersDescription,
    }),
    UpdateProjectParticipantsDescription: tPermission("UpdateProjectParticipantsDescription", {
      defaultValue: defaultTranslations.UpdateProjectParticipantsDescription,
    }),
    UpdateProjectCustomersDescription: tPermission("UpdateProjectCustomersDescription", {
      defaultValue: defaultTranslations.UpdateProjectCustomersDescription,
    }),
    UpdateProjectSuppliersDescription: tPermission("UpdateProjectSuppliersDescription", {
      defaultValue: defaultTranslations.UpdateProjectSuppliersDescription,
    }),
    UpdateProjectWorkspacesDescription: tPermission("UpdateProjectWorkspacesDescription", {
      defaultValue: defaultTranslations.UpdateProjectWorkspacesDescription,
    }),
    UpdateProjectPermissionsDescription: tPermission("UpdateProjectPermissionsDescription", {
      defaultValue: defaultTranslations.UpdateProjectPermissionsDescription,
    }),
    DeleteProjectDescription: tPermission("DeleteProjectDescription", {
      defaultValue: defaultTranslations.DeleteProjectDescription,
    }),
    CreateProjectTasksDescription: tPermission("CreateProjectTasksDescription", {
      defaultValue: defaultTranslations.CreateProjectTasksDescription,
    }),
    UpdateProjectTaskDetailsDescription: tPermission("UpdateProjectTaskDetailsDescription", {
      defaultValue: defaultTranslations.UpdateProjectTaskDetailsDescription,
    }),
    UpdateProjectTaskStatusDescription: tPermission("UpdateProjectTaskStatusDescription", {
      defaultValue: defaultTranslations.UpdateProjectTaskStatusDescription,
    }),
    UpdateProjectTaskManagersDescription: tPermission("UpdateProjectTaskManagersDescription", {
      defaultValue: defaultTranslations.UpdateProjectTaskManagersDescription,
    }),
    UpdateProjectTaskParticipantsDescription: tPermission("UpdateProjectTaskParticipantsDescription", {
      defaultValue: defaultTranslations.UpdateProjectTaskParticipantsDescription,
    }),
    UpdateProjectTaskSuppliersDescription: tPermission("UpdateProjectTaskSuppliersDescription", {
      defaultValue: defaultTranslations.UpdateProjectTaskSuppliersDescription,
    }),
    UpdateProjectTaskWorkspacesDescription: tPermission("UpdateProjectTaskWorkspacesDescription", {
      defaultValue: defaultTranslations.UpdateProjectTaskWorkspacesDescription,
    }),
    DeleteProjectTaskDescription: tPermission("DeleteProjectTaskDescription", {
      defaultValue: defaultTranslations.DeleteProjectTaskDescription,
    }),
    CreateProjectCalendarEventsDescription: tPermission("CreateProjectCalendarEventsDescription", {
      defaultValue: defaultTranslations.CreateProjectCalendarEventsDescription,
    }),
    CreateProjectTodosDescription: tPermission("CreateProjectTodosDescription", {
      defaultValue: defaultTranslations.CreateProjectTodosDescription,
    }),
    CreateSubTasksDescription: tPermission("CreateSubTasksDescription", {
      defaultValue: defaultTranslations.CreateSubTasksDescription,
    }),
    UpdateTaskDetailsDescription: tPermission("UpdateTaskDetailsDescription", {
      defaultValue: defaultTranslations.UpdateTaskDetailsDescription,
    }),
    UpdateTaskStatusDescription: tPermission("UpdateTaskStatusDescription", {
      defaultValue: defaultTranslations.UpdateTaskStatusDescription,
    }),
    UpdateTaskManagersDescription: tPermission("UpdateTaskManagersDescription", {
      defaultValue: defaultTranslations.UpdateTaskManagersDescription,
    }),
    UpdateTaskParticipantsDescription: tPermission("UpdateTaskParticipantsDescription", {
      defaultValue: defaultTranslations.UpdateTaskParticipantsDescription,
    }),
    UpdateTaskSuppliersDescription: tPermission("UpdateTaskSuppliersDescription", {
      defaultValue: defaultTranslations.UpdateTaskSuppliersDescription,
    }),
    UpdateTaskWorkspacesDescription: tPermission("UpdateTaskWorkspacesDescription", {
      defaultValue: defaultTranslations.UpdateTaskWorkspacesDescription,
    }),
    DeleteTaskDescription: tPermission("DeleteTaskDescription", {
      defaultValue: defaultTranslations.DeleteTaskDescription,
    }),
    CreateTaskCalendarEventsDescription: tPermission("CreateTaskCalendarEventsDescription", {
      defaultValue: defaultTranslations.CreateTaskCalendarEventsDescription,
    }),
    CreateTaskTodosDescription: tPermission("CreateTaskTodosDescription", {
      defaultValue: defaultTranslations.CreateTaskTodosDescription,
    }),
    UpdateAnyRoleDescription: tPermission("UpdateAnyRoleDescription", {
      defaultValue: defaultTranslations.UpdateAnyRoleDescription,
    }),
    ManageProjectNotificationsDescription: tPermission("ManageProjectNotificationsDescription", {
      defaultValue: defaultTranslations.ManageProjectNotificationsDescription,
    }),
    yesBtnLabel: t("yesLabel", { defaultValue: defaultTranslations.yesBtnLabel }),
    noBtnLabel: t("noLabel", { defaultValue: defaultTranslations.noBtnLabel }),
    ViewProjectAttachmentsDescription: tPermission("ViewProjectAttachmentsDescription", {
      defaultValue: defaultTranslations.ViewProjectAttachmentsDescription,
    }),
    UpdateProjectAttachmentsDescription: tPermission("UpdateProjectAttachmentsDescription", {
      defaultValue: defaultTranslations.UpdateProjectAttachmentsDescription,
    }),
    ViewTaskAttachmentsDescription: tPermission("ViewTaskAttachmentsDescription", {
      defaultValue: defaultTranslations.ViewTaskAttachmentsDescription,
    }),
    UpdateTaskAttachmentsDescription: tPermission("UpdateTaskAttachmentsDescription", {
      defaultValue: defaultTranslations.UpdateTaskAttachmentsDescription,
    }),
    ViewAnyProjectAttachmentsDescription: tPermission("ViewAnyProjectAttachmentsDescription", {
      defaultValue: defaultTranslations.ViewAnyProjectAttachmentsDescription,
    }),
    UpdateAnyProjectAttachmentsDescription: tPermission("UpdateAnyProjectAttachmentsDescription", {
      defaultValue: defaultTranslations.UpdateAnyProjectAttachmentsDescription,
    }),
    ViewAnyTaskAttachmentsDescription: tPermission("ViewAnyTaskAttachmentsDescription", {
      defaultValue: defaultTranslations.ViewAnyTaskAttachmentsDescription,
    }),
    UpdateAnyTaskAttachmentsDescription: tPermission("UpdateAnyTaskAttachmentsDescription", {
      defaultValue: defaultTranslations.UpdateAnyTaskAttachmentsDescription,
    }),
    hourReportPermissionsHeader: tPermission("hourReportPermissionsHeader", {
      defaultValue: defaultTranslations.hourReportPermissionsHeader,
    }),
    ReportOwnHoursAsMemberDescription: tPermission("ReportOwnHoursAsMemberDescription", {
      defaultValue: defaultTranslations.ReportOwnHoursAsMemberDescription,
    }),
    ReportOwnHoursAnywhereDescription: tPermission("ReportOwnHoursAnywhereDescription", {
      defaultValue: defaultTranslations.ReportOwnHoursAnywhereDescription,
    }),
    ReportOthersHoursAsMemberDescription: tPermission("ReportOthersHoursAsMemberDescription", {
      defaultValue: defaultTranslations.ReportOthersHoursAsMemberDescription,
    }),
    ReportOthersHoursAnywhereDescription: tPermission("ReportOthersHoursAnywhereDescription", {
      defaultValue: defaultTranslations.ReportOthersHoursAnywhereDescription,
    }),
    ViewAnyHourReportDescription: tPermission("ViewAnyHourReportDescription", {
      defaultValue: defaultTranslations.ViewAnyHourReportDescription,
    }),
  }
}

export default useTranslations
